import { useEffect } from "react";
import { CssBaseline, StyledEngineProvider } from "@mui/material";
import { IntlProvider } from "react-intl";
import { RouterProvider } from "react-router-dom";
import { router } from "../../router";
import { ApolloProvider } from "@apollo/client";
import { Provider } from "react-redux";
import ContextDataFetcher from "./ContextDataFetcher";
import { apolloClient, reduxStore } from "../../global";
import { useTranslation } from "../../context/translation";
import ThemeProvider from "../../containers/ThemeProvider";
import { Logger, LoggerContext } from "@dvrt/web-phone-client";
import { conferenceDebugSettingsStorage, DebugSettingsProvider, useDebugSettings } from "conference/tools";
import debug from "debug";
import EnsureAuthenticatedUser from "@/components/EnsureAuthenticatedUser";

const Translations = ({ children }: { children: React.ReactNode }) => {
    const translations = useTranslation();

    return (
        <IntlProvider locale={translations.locale} messages={translations.translations}>
            {children}
        </IntlProvider>
    );
};

const DebugLogger = () => {
    const debugSettings = useDebugSettings();

    useEffect(() => {
        if (debugSettings.enabled) debug.enable(debugSettings.loggerNamespaces);
        else debug.disable();
    }, [debugSettings.enabled, debugSettings.loggerNamespaces]);

    return <></>;
};

export const rootLogger = new Logger("Conference");

export default () => {
    return (
        <ApolloProvider client={apolloClient}>
            <Provider store={reduxStore}>
                <LoggerContext.Provider value={rootLogger}>
                    <CssBaseline />
                    <StyledEngineProvider injectFirst>
                        <ThemeProvider>
                            <DebugSettingsProvider settings={conferenceDebugSettingsStorage}>
                                <DebugLogger />
                                <ContextDataFetcher>
                                    <EnsureAuthenticatedUser>
                                        <Translations>
                                            <RouterProvider
                                                router={router}
                                                future={{
                                                    v7_startTransition: true,
                                                }}
                                            />
                                        </Translations>
                                    </EnsureAuthenticatedUser>
                                </ContextDataFetcher>
                            </DebugSettingsProvider>
                        </ThemeProvider>
                    </StyledEngineProvider>
                </LoggerContext.Provider>
            </Provider>
        </ApolloProvider>
    );
};
